import "leaflet/dist/leaflet.css";

import lightGreen from "@material-ui/core/colors/lightGreen";
import red from "@material-ui/core/colors/red";
import {
  StyledComponentProps,
  StyleRulesCallback,
  Theme,
  withStyles,
  withTheme,
} from "@material-ui/core/styles";
import * as geojson from "geojson";
import * as L from "leaflet";
import * as _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";

import { Dispatch, StoreState } from "../store";
import { DashboardFilterState, Score } from "../types/index";
import {
  dashboardFiltersPartiallyEqual,
  numberFormat,
  roundNumber,
} from "../utilities";
import MapOverlayHintZoom from "./MapOverlayHintZoom";
import ProgressOverlay from "./ProgressOverlay";

function mapStateToProps({ dashboardFilter, postalcodes }: StoreState) {
  return {
    dashboardFilter,
    postalcodes,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    fetchPostalcodes: (dashboardFilter: DashboardFilterState) =>
      dispatch.postalcodes.fetchPostalcodes(dashboardFilter),
  };
}

interface State {
  showPressCtrlToZoomHint: boolean;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

class HeatMap extends React.Component<
  Props & StyledComponentProps & { theme: Theme },
  State
> {
  public state = { showPressCtrlToZoomHint: false };
  private geojson: L.GeoJSON<any>;
  private map: L.Map;
  private legend = new L.Control({ position: "bottomright" });
  private ctrlKeyDown = false;

  private enableMapOverlayHintZoom = _.throttle(() => {
    if (!this.ctrlKeyDown) {
      this.setState({ showPressCtrlToZoomHint: true });
    }
  }, 100);

  private disableMapOverlayHintZoomAfterTimeout = _.debounce(() => {
    this.setState({ showPressCtrlToZoomHint: false });
  }, 1000);

  private handleOnWheelEvent = () => {
    this.enableMapOverlayHintZoom();
    this.disableMapOverlayHintZoomAfterTimeout();
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.ctrlKey) {
      this.map.scrollWheelZoom.enable();
      this.setState({ showPressCtrlToZoomHint: false });
      this.ctrlKeyDown = true;
    }
  };

  private handleKeyUp = () => {
    this.map.scrollWheelZoom.disable();
    this.ctrlKeyDown = false;
  };

  private createMapLegend(
    colorScale: Array<{ color: string; range: [number, number] }>
  ) {
    this.legend.onAdd = () => {
      const { classes } = this.props;
      const div = L.DomUtil.create("div", `${classes && classes.legend}`);
      let title = "";

      if (this.props.dashboardFilter.score === Score.CHURN) {
        title = "Durchschnittlicher Churn Score<br />je PLZ";
      } else if (this.props.dashboardFilter.score === Score.CLV) {
        title = "Durchschnittlicher CLV<br />je PLZ";
      }

      div.innerHTML += `<h4>${title}</h4>`;
      colorScale.forEach((color) => {
        div.innerHTML +=
          `<i><img style="background:${color.color};width:15px;height:15px;margin-right:10;margin-bottom:-3px"></img>` +
          "&nbsp" +
          color.range[0].toFixed(2) +
          " &ndash; " +
          color.range[1].toFixed(2) +
          `</i><br>`;
      });
      return div;
    };

    this.legend.addTo(this.map);
  }

  public async componentDidMount() {
    this.map = L.map("map", { scrollWheelZoom: false }).setView(
      // Fallback center: Kiel.
      [
        parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || "54.3418925"),
        parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || "10.0554665"),
      ],
      10
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    this.fetchData();
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  public async componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  private getLimitsForColorScale(): [number, number] {
    const featureAvgMap = this.props.postalcodes.features.map(
      (feature) => feature.properties.avg
    );
    return [_.min(featureAvgMap), _.max(featureAvgMap)];
  }

  private getColorScale(minAvg: number, maxAvg: number) {
    const color =
      this.props.dashboardFilter.score === Score.CLV ? lightGreen : red;
    const totalDist = maxAvg - minAvg;
    let subDist: number;
    let colors: string[];
    if (totalDist > 3) {
      subDist = totalDist / 10;
      colors = [
        color[50],
        color[100],
        color[200],
        color[300],
        color[400],
        color[500],
        color[600],
        color[700],
        color[800],
        color[900],
      ];
    } else {
      subDist = totalDist / 5;
      colors = [color[100], color[300], color[500], color[700], color[900]];
    }
    if (totalDist === 0) {
      return [
        { color: color[500], range: [minAvg, maxAvg] as [number, number] },
      ];
    }
    const subRanges: Array<[number, number]> = [];
    let avg = minAvg;
    while (avg < maxAvg) {
      subRanges.push([avg, avg + subDist]);
      avg = avg + subDist;
    }
    return colors
      .map((color, index) => ({ color, range: subRanges[index] }))
      .reverse();
  }

  public async componentDidUpdate(previousProps: Props) {
    if (
      !dashboardFiltersPartiallyEqual(
        previousProps.dashboardFilter,
        this.props.dashboardFilter,
        ["dateRange"]
      )
    ) {
      this.fetchData();
    }

    if (
      previousProps.postalcodes !== this.props.postalcodes &&
      this.props.postalcodes.features.length
    ) {
      const [minAvg, maxAvg] = this.getLimitsForColorScale();
      const colorScale = this.getColorScale(minAvg, maxAvg);
      const style = (feature: any) => {
        return {
          color: this.props.theme.palette.primary.main,
          dashArray: "5",
          fillColor: this.getColor(feature.properties.avg, colorScale),
          fillOpacity: 0.7,
          opacity: 1,
          weight: 3,
        };
      };

      if (this.geojson) {
        this.map.removeLayer(this.geojson);
      }
      this.geojson = L.geoJSON(this.props.postalcodes, {
        onEachFeature: this.onEachFeature,
        style,
      }).addTo(this.map);
      this.createMapLegend(colorScale);
    }
  }

  public render() {
    return (
      <ProgressOverlay loading={this.props.postalcodes.loading}>
        <MapOverlayHintZoom
          showPressCtrlToZoomHint={this.state.showPressCtrlToZoomHint}
          onWheel={this.handleOnWheelEvent}
        >
          <div style={{ height: 600 }} id="map" />
        </MapOverlayHintZoom>
      </ProgressOverlay>
    );
  }

  private getColor = (
    d: number,
    colorScale: Array<{ color: string; range: [number, number] }>
  ) => {
    const matchingColor = _.find(
      colorScale,
      (color) =>
        roundNumber(d, 3) >= roundNumber(color.range[0], 3) &&
        roundNumber(d, 3) <= roundNumber(color.range[1], 3)
    );
    return matchingColor?.color;
  };

  private resetHighlight = (e: any) => {
    this.geojson.resetStyle(e.target);
  };

  private onEachFeature = (
    feature: geojson.Feature<geojson.Geometry, any>,
    layer: L.Layer
  ): void => {
    const { score } = this.props.dashboardFilter;
    const { avg, postcode } = feature.properties;
    layer.bindTooltip(
      `${postcode} (Durchschnitt ${
        score === Score.CHURN ? "Churn Score" : "CLV"
      } ${numberFormat(avg, true)})`
    );
    layer.on({
      mouseout: this.resetHighlight,
      mouseover: this.highlightFeature,
    });
  };

  private highlightFeature = (e: any) => {
    const layer = e.target;

    layer.setStyle({
      color: this.props.theme.palette.primary.main,
      dashArray: "",
      // fillOpacity: 0.7,
      // weight: 5
    });

    if (!L.Browser.ie && !L.Browser.opera12 && !L.Browser.edge) {
      layer.bringToFront();
    }
  };

  private fetchData = async () => {
    this.props.fetchPostalcodes(this.props.dashboardFilter);
  };
}

const styles: StyleRulesCallback<Theme, Props> = (theme: Theme) => ({
  legend: {
    background: "white",
    border: "2px solid black",
    borderRadius: "15px",
    opacity: 1,
    paddingBottom: 10,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 0,
    textAlign: "left",
    width: "150px",
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withTheme(HeatMap)));



// WEBPACK FOOTER //
// ./src/components/HeatMap.tsx